<template>
  <div ref="swiperEl" class="swiper" :class="$style['media-grid']">
    <div class="swiper-wrapper">
      <div v-for="(mediaItem, index) in media" :key="mediaItem.mobile" class="swiper-slide">
        <MediaGridItem
          :key="mediaItem.mobile"
          ref="item"
          :alt="alt"
          :media-item="mediaItem"
          :index="index" />
      </div>
    </div>
    <div ref="scrollBarEl" class="swiper-scrollbar" />
  </div>
</template>
<script setup>
import Swiper from 'swiper'
import { Scrollbar, Grid, Mousewheel, FreeMode } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/scrollbar'
import 'swiper/css/mousewheel'
import 'swiper/css/grid'

const props = defineProps({
  loading: {
    type: Boolean,
    default: false
  },
  images: {
    type: [Array, Object],
    default: null
  },
  imagesMeta: {
    type: Array,
    default: null
  },
  videos: {
    type: Array,
    default: null
  },
  alt: {
    type: String,
    required: false,
    default: null
  },
  inline: {
    type: Boolean,
    default: false
  },
  aspectRatio: {
    type: String,
    default: null
  },
  selectedItem: {
    type: Number,
    default: 1
  }
})

const swiperEl = ref(null)
const scrollBarEl = ref(null)
const init = async () => {
  if (!props.images) { return }
  await nextTick()
  const swiper = new Swiper(swiperEl.value, {
    modules: [Mousewheel, FreeMode, Scrollbar, Grid],
    sticky: true,
    freeMode: {
      sticky: true,
      minimumVelocity: 0.01,
      momentum: true,
      momentumVelocityRatio: 0.1
    },
    scrollbar: {
      el: '.swiper-scrollbar',
      hide: false
    },
    slidesPerView: 1,
    spaceBetween: 0,
    updateOnWindowResize: true,
    mousewheel: {
      forceToAxis: true,
      thresholdDelta: 35
    },
    breakpoints: props.inline
      ? false
      : {
          1024: {
            enabled: false,
            slidesPerView: 2,
            spaceBetween: 32,
            grid: {
              fill: 'row',
              rows: Math.ceil((props.images?.length + (props.videos?.length ? 1 : 0)) / 2)
            }
          }
        }
  })
}
onMounted(init)
onActivated(init)
watch(() => props.images, init)
const config = useRuntimeConfig()

const media = computed(() => {
  if (!props.images) { return }
  const items = props.images.map(item => {
    if (item.includes('//')) { return false }
    if (item.includes('/')) { return { type: 'image', full: item, mobile: item } } // If item is already an url, return it
    if (props.imagesMeta?.length) {
      for (let i = 0; i < props.imagesMeta.length; i++) {
        if (props.imagesMeta[i].imageId === item) {
          return {
            type: 'image',
            full: `${config.public.cdnURL}pictures/hd/${item}.webp`,
            mobile: `${config.public.cdnURL}pictures/full/${item}.webp`,
            meta: props.imagesMeta[i]
          }
        }
      }
    }
    return {
      type: 'image',
      full: `${config.public.cdnURL}pictures/hd/${item}.webp`,
      mobile: `${config.public.cdnURL}pictures/full/${item}.webp`
    }
  })
  if (props.videos) {
    props.videos.forEach((video, index) => {
      items.splice(index + 1, 0, {
        type: 'video',
        id: video
      })
    })
  }
  return items
})
</script>
<style lang="scss" module>
.media-grid {
  --swiper-scrollbar-top: #{$spacing * 2};
  --swiper-scrollbar-sides-offset: #{$spacing * 2};
  --swiper-scrollbar-bottom: auto;
  --swiper-scrollbar-drag-bg-color: #{$white};
}
</style>
